/**
 * GIS Types & Constants
 * Method colors, survey layer models, anomaly types
 */

// Method color mapping — matches GeoStack spec + extended types
export const METHOD_COLORS = {
  'Resistivity':                    '#00d4ff', // cyan
  'Induced Polarization':           '#a78bfa', // purple
  'Spontaneous Potential':          '#34d399', // emerald
  'Spectral IP':                    '#c084fc', // violet
  'Time-domain Electromagnetic':    '#4ade80', // green
  'Frequency-domain Electromagnetic':'#60a5fa', // blue
  'Electromagnetic':                '#60a5fa', // blue
  'Seismic MASW':                   '#fb923c', // orange
  'Seismic ReMi':                   '#f97316', // orange-dark
  'Seismic Reflection':             '#f59e0b', // amber
  'Seismic Refraction':             '#fbbf24', // yellow
  'Magnetic':                       '#e879f9', // fuchsia
  'Ground Penetrating Radar':       '#f87171', // red
  'Nuclear Magnetic Resonance':     '#facc15', // yellow
  'Borehole':                       '#8b949e', // slate
  'Geology':                        '#fb923c', // orange
  'Infrastructure':                 '#6b7280', // gray
  'Raster':                         '#22d3ee', // cyan
  'DEM':                            '#a3e635', // lime
};

// Short labels for compact UI
export const METHOD_SHORT_LABELS = {
  'Resistivity':                    'ERT',
  'Induced Polarization':           'IP',
  'Spontaneous Potential':          'SP',
  'Spectral IP':                    'SIP',
  'Time-domain Electromagnetic':    'TEM',
  'Frequency-domain Electromagnetic':'FDEM',
  'Electromagnetic':                'EM',
  'Seismic MASW':                   'MASW',
  'Seismic ReMi':                   'ReMi',
  'Seismic Reflection':             'Seismic',
  'Seismic Refraction':             'Refraction',
  'Magnetic':                       'Magnetic',
  'Ground Penetrating Radar':       'GPR',
  'Nuclear Magnetic Resonance':     'NMR',
  'Borehole':                       'Borehole',
  'Geology':                        'Geology',
  'Infrastructure':                 'Infra',
  'Raster':                         'Raster',
  'DEM':                            'DEM',
};

// Anomaly severity levels
export const ANOMALY_SEVERITY = {
  high:   { color: '#f87171', bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.25)', label: 'High' },
  medium: { color: '#fbbf24', bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.25)',  label: 'Medium' },
  low:    { color: '#00d4ff', bg: 'rgba(0,212,255,0.12)',   border: 'rgba(0,212,255,0.25)',   label: 'Low' },
};

// Coordinate reference systems
export const COORDINATE_SYSTEMS = [
  { value: 'EPSG:4326',  label: 'WGS84 / Geographic (Lat/Long)' },
  { value: 'EPSG:32617', label: 'UTM Zone 17N (FDOT site)' },
  { value: 'EPSG:32614', label: 'UTM Zone 14N' },
  { value: 'EPSG:2236',  label: 'State Plane Florida East' },
  { value: 'EPSG:4269',  label: 'NAD83' },
  { value: 'EPSG:4267',  label: 'NAD27' },
  { value: 'LOCAL',      label: 'Local site grid' },
  { value: 'EPSG:4978',  label: 'ECEF (3D Cartesian)' },
];

// Topography sources
export const TOPO_SOURCES = [
  'RTK GPS',
  'Differential GPS',
  'Total Station',
  'Handheld GPS',
  'LiDAR (Airborne)',
  'LiDAR (Terrestrial)',
  'Drone Photogrammetry (SfM)',
  'SLAM',
  'DEM — USGS 3DEP',
  'DEM — SRTM',
  'Bathymetric',
];

/**
 * Convert a survey line to a GeoJSON Feature (LineString)
 */
export function surveyLineToGeoJSON(line) {
  return {
    type: 'Feature',
    properties: {
      id: line.id,
      name: line.name,
      method: line.method,
      electrodes: line.electrodes,
      length: line.length,
      color: METHOD_COLORS[line.method] || '#8b949e',
    },
    geometry: {
      type: 'LineString',
      coordinates: [
        [line.start_lon, line.start_lat],
        [line.end_lon, line.end_lat],
      ],
    },
  };
}

/**
 * Generate electrode point GeoJSON along a line at regular spacing
 */
export function generateElectrodePoints(line) {
  const features = [];
  const count = line.electrodes || 8;
  for (let i = 0; i <= count; i++) {
    const t = i / count;
    const lon = line.start_lon + (line.end_lon - line.start_lon) * t;
    const lat = line.start_lat + (line.end_lat - line.start_lat) * t;
    features.push({
      type: 'Feature',
      properties: { lineId: line.id, index: i },
      geometry: { type: 'Point', coordinates: [lon, lat] },
    });
  }
  return features;
}

/**
 * Convert a sounding to a GeoJSON Feature (Point)
 */
export function soundingToGeoJSON(sounding) {
  return {
    type: 'Feature',
    properties: {
      id: sounding.id,
      label: sounding.label,
      method: sounding.method,
      depth: sounding.depth,
      color: METHOD_COLORS[sounding.method] || '#8b949e',
      shape: sounding.method === 'Borehole' ? 'triangle' : 'circle',
    },
    geometry: {
      type: 'Point',
      coordinates: [sounding.lon, sounding.lat],
    },
  };
}

/**
 * Convert an anomaly to a GeoJSON Feature (Point)
 */
export function anomalyToGeoJSON(anomaly) {
  const severity = ANOMALY_SEVERITY[anomaly.severity] || ANOMALY_SEVERITY.low;
  return {
    type: 'Feature',
    properties: {
      id: anomaly.id,
      label: anomaly.label,
      type: anomaly.type,
      description: anomaly.description,
      severity: anomaly.severity,
      color: severity.color,
    },
    geometry: {
      type: 'Point',
      coordinates: [anomaly.lon, anomaly.lat],
    },
  };
}

/**
 * Build a GeoJSON FeatureCollection from an array of features
 */
export function toFeatureCollection(features) {
  return { type: 'FeatureCollection', features };
}
